import React, { useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import {
	ErrorMessage as AkErrorMessage,
	Field as AkField,
	HelperMessage as AkHelperMessage,
} from '@atlaskit/form';
import InlineDialog from '@atlaskit/inline-dialog';
import AkInlineEdit from '@atlaskit/inline-edit';
import TextField from '@atlaskit/textfield';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { INVALID_FORMAT } from '../../../../../../../../../common/constants';
import { ReadViewContainer } from '../../../../../../../../../common/ui/goals/styled';
import {
	toSubGoalDurationFieldName,
	toGoalDurationFieldName,
} from '../../../../../../../../../common/utils/goal/field';
import { validateDuration } from '../../../../../../../../../common/utils/goal/validators/index.tsx';
import { useSLAHierarchicalGoalsEnabled } from '../../../../../../../../../controllers/enable-sla-hierarchical-goals';
import messages from './messages';

type Props = {
	isEditing: boolean;
	focusOnEdit?: boolean;
} & {
	id: string;
	goalId?: string;
	duration: string;
	onEditRequested: () => void;
	onChange?: () => void;
};

// render read-only -or editable-field

export const DurationFieldView = ({
	id,
	goalId,
	isEditing,
	focusOnEdit = false,
	onEditRequested,
	duration,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onChange = () => {},
}: Props) => {
	const { formatMessage } = useIntl();
	const [{ isSLAHierarchicalGoalsEnabled }] = useSLAHierarchicalGoalsEnabled();
	let fieldName;
	if (isSLAHierarchicalGoalsEnabled) {
		fieldName = toSubGoalDurationFieldName(goalId, id);
	} else {
		fieldName = toGoalDurationFieldName(id, isEditing);
	}

	const debouncedOnChange = debounce(onChange, 1500);
	const validateDurationFn = (timeGoalValue: string) =>
		validateDuration(timeGoalValue, formatMessage);
	const inputRef = useRef<unknown>(null);
	useEffect(() => {
		if (isEditing) {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			focusOnEdit && inputRef.current?.focus();
		}
	}, [focusOnEdit, isEditing]);
	return (
		// @ts-expect-error - TS2322 - Type '(timeGoalValue: string) => string | undefined' is not assignable to type '(value: string | undefined, formState: Object, fieldState: Meta) => string | void | Promise<string | void>'.
		<AkField name={fieldName} defaultValue={duration} validate={validateDurationFn}>
			{({ fieldProps, error }) => (
				<>
					{/* @ts-expect-error - TS2741 - Property 'defaultValue' is missing in type '{ isEditing: boolean; editView: ({ errorMessage, isInvalid }: ExtendedFieldProps<string>) => Element; readView: () => Element; onEdit: () => void; onConfirm: () => void; hideActionButtons: true; readViewFitContainerWidth: true; }' but required in type 'InlineEditProps<string>'. */}
					<AkInlineEdit
						isEditing={isEditing}
						editView={({ errorMessage, isInvalid }) =>
							ff('fix-jsma11y-980-issue') ? (
								<InlineDialog isOpen={isInvalid} content={<>{errorMessage}</>} placement="right">
									<div aria-labelledby="accessible-id-for-time-target">
										<TextField
											ref={inputRef}
											aria-label={formatMessage(messages.selectDuration)}
											{...fieldProps}
											placeholder={
												isSLAHierarchicalGoalsEnabled
													? formatMessage(messages.openTipForGoalDurationFormat)
													: ''
											}
											testId="servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.goal.fields.duration.ak-single-line-text-input"
											onChange={(...args) => {
												fieldProps.onChange(...args);
												debouncedOnChange();
											}}
											appearance={isSLAHierarchicalGoalsEnabled ? 'subtle' : 'standard'}
										/>
									</div>
								</InlineDialog>
							) : (
								<InlineDialog isOpen={isInvalid} content={<>{errorMessage}</>} placement="right">
									<TextField
										ref={inputRef}
										aria-label={formatMessage(messages.selectDuration)}
										{...fieldProps}
										placeholder={
											isSLAHierarchicalGoalsEnabled
												? formatMessage(messages.openTipForGoalDurationFormat)
												: ''
										}
										testId="servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.goal.fields.duration.ak-single-line-text-input"
										onChange={(...args) => {
											fieldProps.onChange(...args);
											debouncedOnChange();
										}}
										appearance={isSLAHierarchicalGoalsEnabled ? 'subtle' : 'standard'}
									/>
								</InlineDialog>
							)
						}
						readView={() => (
							// @ts-expect-error - TS2741 - Property 'placeholder' is missing in type '{ children: string; editMode: true; "data-test-id": string; }' but required in type 'Readonly<ThemedOuterStyledProps<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { editMode: boolean; placeholder: any; }, any>>'.
							<ReadViewContainer
								editMode
								data-testid="servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.goal.fields.duration.read-view"
							>
								{fieldProps.value || formatMessage(messages.noTarget)}
							</ReadViewContainer>
						)}
						onEdit={onEditRequested}
						// eslint-disable-next-line @typescript-eslint/no-empty-function
						onConfirm={() => {}}
						hideActionButtons
						readViewFitContainerWidth
					/>
					<>
						{error === INVALID_FORMAT ? (
							<AkErrorMessage data-testid="servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.goal.fields.duration.ak-error-message">
								{formatMessage(messages.invalidDurationFormat)}
							</AkErrorMessage>
						) : (
							!isSLAHierarchicalGoalsEnabled &&
							isEditing && (
								<AkHelperMessage>
									{formatMessage(messages.openTipForGoalDurationFormat)}
								</AkHelperMessage>
							)
						)}
					</>
				</>
			)}
		</AkField>
	);
};

export const DurationField = DurationFieldView;
